<template>
  <div class="smaller-container-wrapper">
    <MobialisFormWithValidation
      @onValidate="submit"
      novalidate
      v-av-analytics-form-submit="{ elementText: $t('message.common.continue') }"
    >
      <h2>{{ $t('message.eCommerceV2.confirmCreditFormHeading') }}</h2>
      <p
        class="pb-2"
        v-html-safe="
          $t('message.eCommerceV2.confirmCreditFormMsg', {
            creditProvider: this.getDefaultCreditDataSourceCAName.niceName,
          })
        "
      ></p>
      <InfoCard class="font-weight-bold text-center mt-4 mb-5">
        <img class="mr-2 mb-1" :src="require('@/assets/id-icon.svg')" alt="" width="16px" height="14px" />
        {{ $t('message.eCommerceV2.infoMustMatch') }}
      </InfoCard>
      <h3 class="mt-4">
        {{ $t('message.eCommerceV2.confirmCreditFormSubHeading') }}
        <mobialis-button
          v-if="!editingPersonalInformation"
          class="p-0 float-right"
          @click="editingPersonalInformation = true"
          variant="link"
          width="w-auto"
          >{{ $t('message.common.edit') }}</mobialis-button
        >
      </h3>
      <template v-if="!editingPersonalInformation">
        <p class="mb-0">{{ firstName }} {{ lastName }}</p>
        <p class="mb-0 mt-2">{{ helperFunctions.phoneNumberShaper(phoneNumber) }}</p>
        <p class="mb-0 mt-2">{{ emailAddress }}</p>
        <p class="mb-0 mt-2">{{ $t('message.credit.birthday') }}: {{ birthdate }}</p>
      </template>
      <template v-else>
        <div class="row form-row">
          <div class="col-sm-6">
            <mobialis-text-input-with-validation
              :rules="{ required: true, regex: /^([^0-9]*)$/ }"
              :label="$t('message.common.firstName')"
              v-model="firstName"
              vid="firstName"
              data-cy="firstName"
            ></mobialis-text-input-with-validation>
          </div>
          <div class="col-sm-6">
            <mobialis-text-input-with-validation
              :rules="{ required: true, regex: /^([^0-9]*)$/ }"
              :label="$t('message.common.lastName')"
              v-model="lastName"
              vid="lastName"
              data-cy="lastName"
            ></mobialis-text-input-with-validation>
          </div>
        </div>
        <div class="row form-row">
          <div class="col-sm-4">
            <mobialis-text-input-with-validation
              class="input-width-180"
              rules="required|length:10"
              :label="$t('message.common.phoneNumber')"
              v-model="phoneNumber"
              vid="phoneNumber"
              placeholder="(   )"
              mask-type="phoneNumber"
              type="tel"
              data-cy="phoneNumber"
              :error-message="avErrors['phoneNumber']"
            ></mobialis-text-input-with-validation>
          </div>
          <div class="col-sm-8">
            <mobialis-text-input-with-validation
              rules="required|email"
              :label="$t('message.common.emailAddress')"
              v-model="emailAddress"
              vid="emailAddress"
              data-cy="emailAddress"
              type="email"
              :error-message="avErrors['emailAddress']"
            ></mobialis-text-input-with-validation>
          </div>
        </div>
        <div class="row form-row">
          <div class="col-sm-4">
            <mobialis-text-input-with-validation
              class="input-width-180"
              :rules="birthDateValidationRule"
              :label="$t('message.credit.birthday')"
              v-model="birthdate"
              mask-type="date"
              :raw="false"
              inputmode="numeric"
              vid="birthdate"
              data-cy="birthdate"
              :helper="$t('message.credit.birthdayHelper')"
            >
            </mobialis-text-input-with-validation>
          </div>
        </div>
      </template>
      <div class="mt-3 pt-2"></div>
      <hr class="pb-2" />
      <h3 class="mt-4">
        {{ $t('message.common.address') }}
        <mobialis-button
          v-if="!editingAddress"
          class="p-0 float-right"
          @click="editingAddress = true"
          variant="link"
          width="w-auto"
          >{{ $t('message.common.edit') }}</mobialis-button
        >
      </h3>
      <template v-if="!editingAddress">
        <p class="mb-0">
          <span v-if="unitNumber">{{ unitNumber }}-</span>{{ streetAddress }}
        </p>
        <template v-if="!getThisIsAmerica">
          <p class="mb-0 mt-2">{{ city }}, {{ province }}</p>
          <p class="mb-0 mt-2">{{ postalCode }}</p>
        </template>
        <template v-else>
          <p class="mb-0 mt-2">{{ city }}, {{ state }}</p>
          <p class="mb-0 mt-2">{{ zipCode }}</p>
        </template>
      </template>
      <template v-else>
        <div class="row form-row">
          <div class="col-sm-8">
            <mobialis-text-input-with-validation
              rules="required"
              :label="$t('message.credit.streetAddress')"
              v-model="streetAddress"
              vid="streetAddress"
              data-cy="streetAddress"
            ></mobialis-text-input-with-validation>
          </div>
          <div class="col-sm-4">
            <mobialis-text-input-with-validation
              :label="$t('message.credit.unit#')"
              v-model="unitNumber"
              vid="unitNumber"
              data-cy="unitNumber"
            ></mobialis-text-input-with-validation>
          </div>
        </div>
        <div class="row form-row">
          <div class="col-sm-6">
            <mobialis-text-input-with-validation
              rules="required"
              :label="$t('message.common.city')"
              v-model="city"
              vid="city"
              data-cy="city"
            ></mobialis-text-input-with-validation>
          </div>
          <div class="col-sm-6">
            <mobialis-selection-input-with-validation
              v-if="!getThisIsAmerica"
              rules="required"
              :label="$t('message.common.province')"
              v-model="province"
              vid="province"
              :placeHolder="$t('message.common.chooseProvince')"
              :options="provinces"
              data-cy="province"
            ></mobialis-selection-input-with-validation>
            <mobialis-selection-input-with-validation
              v-else
              rules="required"
              :label="$t('message.common.state')"
              v-model="state"
              vid="state"
              :placeHolder="$t('message.common.chooseState')"
              :options="states"
              data-cy="state"
            ></mobialis-selection-input-with-validation>
          </div>
        </div>
        <mobialis-text-input-with-validation
          v-if="!getThisIsAmerica"
          class="input-width-100 mb-0"
          rules="required|length:6"
          :label="$t('message.common.postalCode')"
          v-model="postalCode"
          vid="postalCode"
          mask-type="postalCode"
          data-cy="postalCode"
        ></mobialis-text-input-with-validation>
        <mobialis-text-input-with-validation
          v-else
          class="input-width-100 mb-0"
          rules="required|length:5"
          :label="$t('message.common.zipCode')"
          v-model="zipCode"
          vid="zipCode"
          mask-type="zipCode"
          data-cy="zipCode"
        ></mobialis-text-input-with-validation>
      </template>
      <div class="mt-3 pt-2"></div>
      <hr class="pb-2" />
      <h3 class="mt-4">{{ $t('message.common.termsOfService') }}</h3>
      <mobialis-check-box-with-validation
        class="mt-3"
        rules="required:true"
        :label="
          $t('message.eCommerceV2.creditTandC', {
            termsLink: $t('message.eCommerceV2.termsLink'),
            dealerName: businessName,
            creditProvider: this.getDefaultCreditDataSourceCAName.niceNameV2,
          })
        "
        vid="tandc"
        id="tandc"
        data-cy="tAndC"
        v-model="tandc"
      >
      </mobialis-check-box-with-validation>
      <mobialis-button type="submit" class="my-5" data-cy="continue">{{
        $t('message.common.continue')
      }}</mobialis-button>
    </MobialisFormWithValidation>
    <tool-tip :title="tooltipTitle" :body="tooltipBody"></tool-tip>
  </div>
</template>

<script>
import cloneDeep from 'lodash/fp/cloneDeep';
import { mapActions, mapGetters } from 'vuex';
import { provinceMixin, avErrorMixin, enumMixin, stateMixin, formMixin } from '@/mixins';
import ToolTip from '@/components/ToolTip.vue';
import InfoCard from '@/components/InfoCard.vue';
import log from '@/services/log';
import { AVAnalyticsEventTriggers } from '@/utils/enums';
import helperFunctions from '../../utils/helperFunctions';

export default {
  name: 'ConfirmCreditForm',
  mixins: [provinceMixin, avErrorMixin, enumMixin, stateMixin, formMixin],
  components: {
    ToolTip,
    InfoCard,
  },
  beforeRouteEnter: helperFunctions.onlyAcceptRoutes(['contact-details-form', 'date-of-birth-form']),
  data() {
    return {
      firstName: cloneDeep(this.$store.state.user.firstName),
      lastName: cloneDeep(this.$store.state.user.lastName),
      phoneNumber: cloneDeep(this.$store.state.user.phoneNumber),
      emailAddress: cloneDeep(this.$store.state.user.emailAddress),
      birthdate: cloneDeep(this.$store.state.user.birthdate),
      streetAddress: cloneDeep(this.$store.state.user.streetAddress),
      unitNumber: cloneDeep(this.$store.state.user.unit),
      city: cloneDeep(this.$store.state.user.city),
      province: cloneDeep(this.$store.state.user.province),
      postalCode: cloneDeep(this.$store.state.user.postalCode),
      state: cloneDeep(this.$store.state.user.state),
      zipCode: cloneDeep(this.$store.state.user.zipCode),
      tandc: false,
      editingPersonalInformation: false,
      editingAddress: false,
      helperFunctions,
      customErrorMessage: null,
    };
  },
  computed: {
    ...mapGetters({
      businessName: 'business/getBusinessName',
      getThisIsAmerica: 'business/getThisIsAmerica',
      getCurrentProduct: 'getCurrentProduct',
      getDefaultCreditDataSourceCAName: 'ecommerceV2/getDefaultCreditDataSourceCAName',
    }),
    tooltipTitle() {
      return this.getDefaultCreditDataSourceCAName.niceName;
    },
    tooltipBody() {
      return this.$t('message.eCommerceV2.creditProviderToolTipBody', {
        creditProvider: this.getDefaultCreditDataSourceCAName.niceName,
      });
    },
    isEcomV2() {
      return this.getCurrentProduct === this.enums.AVRProducts.ECOMV2;
    },
  },
  methods: {
    ...mapActions(['credit/getQuestions', 'credit/setQuestions', 'credit/getRegionalCreditScore']),
    async submit(isValid) {
      if (!isValid) {
        return;
      }
      this.customErrorMessage = null;
      this.$store.commit('SET_LOADING', true);
      this.$store.commit('user/SET_FIRST_NAME', this.firstName);
      this.$store.commit('user/SET_LAST_NAME', this.lastName);
      this.$store.commit('user/SET_PHONE_NUMBER', this.phoneNumber);
      this.$store.commit('user/SET_EMAIL_ADDRESS', this.emailAddress);
      this.$store.commit('user/SET_BIRTHDATE', this.birthdate);
      this.$store.commit('user/SET_STREET_ADDRESS', this.streetAddress);
      this.$store.commit('user/SET_UNIT', this.unitNumber);
      this.$store.commit('user/SET_CITY', this.city);
      if (!this.getThisIsAmerica) {
        this.$store.commit('user/SET_PROVINCE_CODE', this.getProvinceCode(this.province));
        this.$store.commit('user/SET_PROVINCE', this.province);
        this.$store.commit('user/SET_POSTAL_CODE', this.postalCode);
      } else {
        this.$store.commit('user/SET_STATE', this.state);
        this.$store.commit('user/SET_STATE_CODE', this.getStateCode(this.state));
        this.$store.commit('user/SET_ZIP_CODE', this.zipCode);
      }
      this.createLead();
    },
    async createLead() {
      const payload = this.isEcomV2 ? { subType: this.enums.SubType.ECOMV2 } : null;
      try {
        await this.$store.dispatch('lead/createLead', payload);
        this.getCredit();
      } catch (err) {
        this.$store.commit('SET_LOADING', false);
        if (!this.handleServerSideError(err)) {
          log('Confirm credit form  - Create Lead', err);
          this.$router.push('error');
        } else {
          this.editingPersonalInformation = true;
        }
      }
    },
    async getCredit() {
      try {
        const response = await this['credit/getQuestions']();
        await this['credit/setQuestions'](response.data);
        this.$router.push({ path: this.getNextRoutePath() });
      } catch (err) {
        // if we don't get questions this is the end of the credit flow
        // so trigger the flow finished event
        helperFunctions.sendAVAnalyticsEvent(this.$route.path, AVAnalyticsEventTriggers.FORM_SUBMIT, this.$store);
        log('Confirm credit form credit/getQuestions and credit/setQuestions', err);
        // Any errors just get regional credit score instead, canada only
        if (!this.getThisIsAmerica) {
          try {
            await this['credit/getRegionalCreditScore']();
            this.$router.push({ path: this.getNextRoutePath() });
          } catch (error) {
            log('Confirm credit form getRegionalCreditScore', error);
            this.creditFailed();
          }
        } else {
          this.creditFailed();
        }
      } finally {
        this.$store.commit('SET_LOADING', false);
      }
    },
    creditFailed() {
      this.$store.commit('credit/SET_STATE', ['creditChecksFailed', true]);
      this.$router.push('credit-error');
    },
  },
};
</script>

<style lang="scss" scoped></style>
